import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ws } from '@/lib/ws';
import type { CredentialMeta } from '@shared/protocol';
import { relativeTime } from '@/lib/utils';

type Provider = CredentialMeta['provider'];

const PROVIDER_DEFAULTS: Record<Provider, { issuer: string; subjectExample: string }> = {
  github: {
    issuer: 'https://token.actions.githubusercontent.com',
    subjectExample: 'repo:your-org/your-repo:ref:refs/heads/main',
  },
  gitlab: {
    issuer: 'https://gitlab.com',
    subjectExample: 'project_path:your-group/your-repo:ref_type:branch:ref:main',
  },
  aws: {
    issuer: 'https://oidc.eks.us-east-1.amazonaws.com/id/XXXXXXXXXXXX',
    subjectExample: 'system:serviceaccount:default:my-pod',
  },
  google: {
    issuer: 'https://accounts.google.com',
    subjectExample: 'my-service-account@my-project.iam.gserviceaccount.com',
  },
};

export function CredentialsPage() {
  const [credentials, setCredentials] = useState<CredentialMeta[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [provider, setProvider] = useState<Provider>('github');
  const [issuer, setIssuer] = useState(PROVIDER_DEFAULTS.github.issuer);
  const [subject, setSubject] = useState('');

  useEffect(() => {
    const off = ws.on('creds.state', (m) => setCredentials(m.credentials));
    const offCreated = ws.on('creds.created', (m) => {
      setCredentials((prev) => [m.credential, ...prev]);
      setShowCreate(false);
      setName('');
      setSubject('');
    });
    const offRevoked = ws.on('creds.revoked', (m) => {
      setCredentials((prev) =>
        prev.map((c) => (c.id === m.id ? { ...c, revoked_at: Date.now() } : c)),
      );
    });
    const offErr = ws.on('error', (m) => {
      if (m.req_type.startsWith('creds.')) setError(m.message);
    });
    ws.send({ type: 'creds.list' });
    return () => {
      off();
      offCreated();
      offRevoked();
      offErr();
    };
  }, []);

  useEffect(() => {
    setIssuer(PROVIDER_DEFAULTS[provider].issuer);
  }, [provider]);

  function handleCreate() {
    ws.send({
      type: 'creds.create',
      name: name.trim(),
      provider,
      oidc_issuer: issuer.trim(),
      subject_claim: subject.trim(),
    });
  }

  function handleRevoke(id: string) {
    if (!confirm('Revoke this credential? CI/CD jobs using it will fail.')) return;
    ws.send({ type: 'creds.revoke', id });
  }

  return (
    <div className="max-w-5xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">OIDC Credentials</h1>
          <p className="text-sm text-muted-foreground">
            Workload identity trust configs. No secrets stored — your CI presents a short-lived
            token, we verify it.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>New credential</Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Your credentials</CardTitle>
        </CardHeader>
        <CardContent>
          {credentials.length === 0 ? (
            <p className="text-sm text-muted-foreground">No credentials yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {credentials.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{c.provider}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs truncate max-w-xs">
                      {c.subject_claim}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {relativeTime(c.last_used)}
                    </TableCell>
                    <TableCell>
                      {c.revoked_at ? (
                        <Badge variant="destructive">revoked</Badge>
                      ) : (
                        <Badge variant="success">active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {!c.revoked_at && (
                        <Button variant="ghost" size="sm" onClick={() => handleRevoke(c.id)}>
                          Revoke
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Create OIDC credential</DialogTitle>
            <DialogDescription>
              Trust a specific CI workflow to authenticate without a stored secret.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 my-4">
            <div className="space-y-1.5">
              <Label htmlFor="credname">Name</Label>
              <Input
                id="credname"
                placeholder="acme-backend-main"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="provider">Provider</Label>
              <Select
                id="provider"
                value={provider}
                onChange={(e) => setProvider(e.target.value as Provider)}
              >
                <option value="github">GitHub Actions</option>
                <option value="gitlab">GitLab CI</option>
                <option value="aws">AWS (IAM OIDC)</option>
                <option value="google">Google Cloud</option>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="issuer">OIDC issuer</Label>
              <Input id="issuer" value={issuer} onChange={(e) => setIssuer(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="subject">Subject claim</Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={PROVIDER_DEFAULTS[provider].subjectExample}
              />
              <p className="text-xs text-muted-foreground">
                Example: <code>{PROVIDER_DEFAULTS[provider].subjectExample}</code>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!name.trim() || !issuer.trim() || !subject.trim()}
            >
              Create credential
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
